import { createContext, useContext } from "react";
import type { VideoCallingSlice, VideoCallingSession } from "../types";

// ============================================
// Context Type
// ============================================

/**
 * Context value that provides the video calling hook and session.
 *
 * This context ONLY provides a hook reference and session, NOT state.
 * When state changes, components re-render through Zustand's subscription,
 * not through context updates.
 */
export interface VideoCallingHookContextValue {
  /** The app's hook that selects videoCalling from their Zustand store */
  useVideoCalling: () => VideoCallingSlice;
  /** The app's session for API calls */
  session: VideoCallingSession | null;
}

// ============================================
// Context
// ============================================

export const VideoCallingHookContext = createContext<VideoCallingHookContextValue | null>(null);

// ============================================
// Hooks
// ============================================

/**
 * Access the raw context value.
 * Prefer using `useVideoCalling` or `useVideoCallingSession` instead.
 */
export function useVideoCallingHookContext(): VideoCallingHookContextValue {
  const context = useContext(VideoCallingHookContext);
  if (!context) {
    throw new Error("useVideoCallingHookContext must be used within VideoCallingHookProvider");
  }
  return context;
}

/**
 * Access the video calling state and actions from the app's Zustand store.
 *
 * This hook returns the video calling slice, which contains both state and actions.
 * State changes are managed by Zustand, ensuring optimal re-render performance.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const videoCalling = useVideoCalling();
 *
 *   return (
 *     <div>
 *       <p>Status: {videoCalling.connectionState}</p>
 *       <button onClick={() => videoCalling.setIsVisible(true)}>
 *         Open Video Call
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useVideoCalling(): VideoCallingSlice {
  const { useVideoCalling: useHook } = useVideoCallingHookContext();
  return useHook();
}

/**
 * Access the session for making API calls.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const session = useVideoCallingSession();
 *
 *   const handleStartMeeting = async () => {
 *     if (session) {
 *       await session.api.meetings.start_meeting_for_event({ calendarEventId });
 *     }
 *   };
 * }
 * ```
 */
export function useVideoCallingSession(): VideoCallingSession | null {
  const { session } = useVideoCallingHookContext();
  return session;
}
